'use client';

import React from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { BasicProductDataResponse, DiscountTypeEnums } from '@/types/catalog';
import Image from '@/components/Image';
import { PRODUCT_IMAGE_SIZE } from '@/lib/utils/command';
import { useParams } from 'next/navigation';
import { Routes } from '@/lib/routes/routes';
import IsFavorite from '../../../components/IsFavorite';
import Promotion from '@/public/assets/Promotion';
import SAR from '@/public/SAR';
import SaudiRealls from '@/components/sarComponent';
import { trackEvent } from '@/lib/amplitude';
import { AmplitudeEvents } from '@/lib/amplitude/events';
import { Button } from '@/components/ui/button';

import Modal from './Modal';

interface ProductCardHandlers {
  scroll?: boolean;
  isFavorite?: boolean;
  isSearchPage?: boolean;
  branchId?: string | number;
}

export function ProductCard({
  productDetails,
  scroll = false,
  isSearchPage = false,
  branchId,
}: {
  productDetails: BasicProductDataResponse;
} & ProductCardHandlers) {
  const {
    defaultImage,
    isHaveValidDiscount,
    discount,
    price,
    englishName,
    arabicName,
    originalPrice,
    isSoldOut,
    storeName,
  } = productDetails as BasicProductDataResponse;

  const { storeId } = useParams();
  const currentLang = useLocale() as 'ar' | 'en';
  const t = useTranslations('product');

  const imageSizes = scroll
    ? '(min-width: 1080px) 268px, (min-width: 780px) 188px, 168px'
    : '(min-width: 1540px) 285px, (min-width: 1080px) calc(16.36vw + 36px), (min-width: 780px) 25vw, (min-width: 640px) 33.33vw, calc(50vw - 8px)';

  return (
    <figure className="flex h-full w-full flex-col justify-between overflow-hidden rounded-xl bg-white transition-shadow hover:shadow-xl">
      <Link
        href={Routes.productDetails(
          Number(productDetails.branchId || storeId || branchId),
          productDetails.integrationId,
        )}
        onClick={() => {
          trackEvent(AmplitudeEvents.PRODUCT_CLICKED, {
            storeId: Number(storeId),
            storeName: productDetails.storeName,
            branchId: Number(productDetails.branchId),
            productId: productDetails.integrationId,
            productName: currentLang === 'en' ? englishName : arabicName,
          });
        }}
      >
        <div title={t('View Product Details')}>
          {/* image info with fav and souldout and descound  */}

          <div
            className={`relative w-full ${scroll || isSearchPage ? 'aspect-square' : 'aspect-4/5'}`}
          >
            <Image
              size={PRODUCT_IMAGE_SIZE.MEDIUM}
              alt={`${productDetails.englishName}`}
              src={defaultImage || ''}
              fill
              className="rounded-xl object-cover"
              sizes={imageSizes}
            />

            {/* add overlay  */}
            {isSoldOut && <div className="bg-primary/50 absolute z-20 h-full w-full" />}

            <div className="absolute flex h-full w-full flex-col justify-between">
              {/* Start: Status and Add to Favorite button and Discounted status */}
              <div
                className="grid"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <div className="absolute right-0 bottom-0 z-30 m-2 grid h-10 w-10 place-items-center rounded-full bg-white/60 shadow-md backdrop-blur-xs">
                  <IsFavorite
                    branch_id={
                      productDetails?.branchId ?? storeId?.toString() ?? branchId?.toString() ?? ''
                    }
                    item_id={productDetails.integrationId}
                    size={24}
                    type={1}
                  />
                </div>

                {/* Start: Discount status */}
                {isHaveValidDiscount && (
                  <div
                    className={`relative z-30 mt-2 ml-2 flex w-full lg:mt-4 lg:ml-4 ltr:justify-start rtl:justify-end`}
                  >
                    <div className="flex gap-1 rounded-md border border-[#9B1D3D] bg-red-700 px-2 py-1 text-xs text-red-100 shadow-xl ltr:flex-row-reverse">
                      {discount?.discountValue}
                      {discount?.discountType === DiscountTypeEnums.Percentage ? (
                        '%'
                      ) : (
                        <SAR width={13} height={13} className={`h-auto w-auto`} />
                      )}
                      <Promotion />
                    </div>
                  </div>
                )}
                {/* End: Discount status */}

                {/* start slod out section  */}
                {isSoldOut && (
                  <div
                    className={`z-30 mt-2 ml-2 flex w-full lg:mt-4 lg:ml-4 ltr:justify-start rtl:justify-end`}
                  >
                    <span className="border-secondary-50 text-gray flex gap-1 rounded-md border bg-gray-300 px-2 py-1 text-xs shadow-xl">
                      {t('SoldOut')}
                    </span>
                  </div>
                )}

                {/* End soldout section  */}
              </div>
            </div>
            {/* End: Over the Image content */}
          </div>

          <div className="flex h-full flex-col p-2 lg:p-3">
            <div>
              <div className="flex items-center justify-between">
                <h2
                  className={`w-full text-xs font-semibold md:text-base ${currentLang === 'en' ? 'text-left' : 'text-right'} `}
                >
                  {currentLang === 'en' ? englishName : arabicName}
                </h2>
              </div>
              {isSearchPage && (
                <h3
                  className={`w-full text-xs font-bold text-[#9F9F9F] ltr:text-left rtl:text-right`}
                >
                  {storeName}
                </h3>
              )}
            </div>

            {/* Start: Price and Add to Cart button */}
            <div className={`mt-1 flex items-center justify-between`}>
              <div className={`flex flex-wrap items-baseline gap-1 text-sm lg:gap-2 lg:text-base`}>
                <div
                  className={`text-sm font-bold lg:text-base ${isHaveValidDiscount && 'text-black'}`}
                >
                  <SaudiRealls price={Number(price?.toFixed(2))} size="medium" />
                </div>
                {isHaveValidDiscount && (
                  <>
                    <div className="text-primary/60 inline-flex items-center gap-1 text-xs font-bold lg:text-sm">
                      <SaudiRealls
                        price={Number(originalPrice?.toFixed(2))}
                        size="medium"
                        isHaveDiscount={true}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Link>
      {/* Add to card  */}
      {isSearchPage && (
        <div className="p-2">
          {isSoldOut ? (
            <Button disabled rounded={'full'}>
              {t('SoldOut')}
            </Button>
          ) : (
            <Modal
              productId={productDetails.integrationId}
              storeId={productDetails.branchId as string}
            />
          )}
        </div>
      )}{' '}
    </figure>
  );
}
